import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Skeleton } from '@mui/material';
import { Maximize2, Play } from 'lucide-mui';

interface ThumbnailProps {
  url: string | null;
  isLoading: boolean;
  title: string;
  onTimeClick: () => void;
  onExpand: () => void;
  sx?: any;
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  url,
  isLoading,
  title,
  onTimeClick,
  onExpand,
  sx,
}) => {
  const [isInView, setIsInView] = React.useState(false);
  const thumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect(); // Once loaded, no need to observe anymore
          }
        });
      },
      {
        rootMargin: '50px', // Start loading when within 50px of viewport
      }
    );

    if (thumbnailRef.current) {
      observer.observe(thumbnailRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      ref={thumbnailRef}
      sx={{
        width: '200px',
        height: '113px',
        backgroundColor: 'background.paper',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        flexShrink: 0,
        '&:hover': {
          '& .hover-actions': {
            opacity: 1,
          },
        },
        ...sx,
      }}
    >
      {isLoading && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
        />
      )}
      {!isLoading && !url && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation={false}
        />
      )}
      {!isLoading && url && isInView && (
        <>
          <img
            src={url}
            alt={`Thumbnail for ${title}`}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />
          {/* Hover Actions */}
          <Box
            className="hover-actions"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              opacity: 0,
              transition: 'opacity 0.2s ease-in-out',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onTimeClick();
              }}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
                '& svg': {
                  color: 'white',
                  stroke: 'white',
                  strokeWidth: 2.5,
                },
              }}
            >
              <Play fontSize="small" />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onExpand();
              }}
              size="small"
              sx={{
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
                '& svg': {
                  color: 'white',
                  stroke: 'white',
                  strokeWidth: 2.5,
                },
              }}
            >
              <Maximize2 fontSize="small" />
            </IconButton>
          </Box>
        </>
      )}
      {!isLoading && url && !isInView && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
        />
      )}
    </Box>
  );
};

export default Thumbnail;
